<script>
	import { onMount } from 'svelte';
	import { Link } from "svelte-routing";
	import { environment } from '../js/utils/environment';
	import { pageOffset, universalScroll, toggleUniversalToPageScroll } from '../js/model/stores';

	import TopNavigation from "../components/TopNavigation.svelte";
	import PureText from "../components/PureText.svelte";
	import BottomNavigation from "../components/BottomNavigation.svelte";

	export let name;


	// left: 37, up: 38, right: 39, down: 40,
	// spacebar: 32, pageup: 33, pagedown: 34, end: 35, home: 36
	const scrollKeys = {37: 1, 38: 1, 39: 1, 40: 1};

	// modern Chrome requires { passive: false } when adding event
	let supportsPassive = false;
	let wheelOpt;
	let wheelEvent;
	let lastTouchClientY;

	let _main;

	/**
	 *
	 * @param {*} event Can be either scroll, touchmove, keydown
	 */
	let calculatePageOffset = (event) => {

		let delta = 0;

		switch (event.type) {
			case 'touchstart':
				lastTouchClientY = event.touches[0].clientY;
				break;
			case 'touchmove':
				delta = (event.touches[0].clientY - lastTouchClientY);
				delta = delta * -1;		// touchmove direction value is contrary to scroll direction

				lastTouchClientY = event.touches[0].clientY;
				break;

			// Do nothing here but prevent the default case. We'll check the keyCode later
			case 'keydown':
				break;

			// All other events (scroll, wheel, mousewheel) should have the deltaY value
			default:
				delta = event.deltaY;
				break;
		}

		if (!event.keyCode) {
			event.preventDefault();
			let lastOffset = $pageOffset;
			let newOffset = Math.max(0, ($pageOffset + delta));

			// Stop scrolling down (positive delta)
			const mainBottomOffset = Math.floor(_main.getBoundingClientRect().bottom - environment().innerHeight());
			const footerHeight = document.querySelector('.bottom_navigation').getBoundingClientRect().height * 0.8;

			// FIXME: Freeze newOffset before doing the last update. Checking the main offset afterwards causes some overscrolling, depending on the scroll speed.

			if (delta > 0 && (mainBottomOffset + footerHeight) < 0) {
				return;
			}

			pageOffset.set(newOffset);
		} else if (scrollKeys[event.keyCode]) {
			event.preventDefault();

			switch (event.keyCode) {
				case 38:
					pageOffset.set($pageOffset - 10);
					break;
				case 40:
					pageOffset.set($pageOffset + 10);
					break;
			}
		}
	};

	let initScrollingMonitor = () => {
		try {
			window.addEventListener("test", null, Object.defineProperty({}, 'passive', {
				get: function () {
					supportsPassive = true;
				}
			}));
		} catch (e) {
		}

		wheelOpt = supportsPassive ? {passive: false} : false;
		wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

		// If the page loads with an window offset (on page reload), it must have been scrolled behind the universal scroll section.
		const initialPageOffset = window.scrollY > 0 ? window.scrollY + $toggleUniversalToPageScroll : 0;

		pageOffset.set(initialPageOffset);
		enableUniversalScrolling();
	};

	let enableUniversalScrolling = () => {
		window.addEventListener('DOMMouseScroll', calculatePageOffset, false); // older FF
		window.addEventListener(wheelEvent, calculatePageOffset, wheelOpt); // modern desktop
		window.addEventListener('touchstart', calculatePageOffset, wheelOpt); // mobile
		window.addEventListener('touchmove', calculatePageOffset, wheelOpt); // mobile
		window.addEventListener('keydown', calculatePageOffset, false);
	};

	let calculateCorrectViewportHeight = () => {
		// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
		let vhInPixel = window.innerHeight * 0.01;

		// Then we set the value in the --vh custom property to the root of the document
		document.documentElement.style.setProperty('--vh', `${vhInPixel}px`);
	};

	let setBaseFontSizeByScreenWidth = () => {

		const BASE_SIZE = 1.2;	// used as the default 'vw' and with calculations

		const vwInPixel = window.innerWidth * 0.01;
		const baseSizeInPixel = vwInPixel * BASE_SIZE;

		if (baseSizeInPixel < 16) {
			document.documentElement.style.setProperty('--base-size', '16px');
		} else if (baseSizeInPixel > 30) {
			document.documentElement.style.setProperty('--base-size', '30px');
		} else {
			document.documentElement.style.setProperty('--base-size', `${BASE_SIZE}vw`);
		}
	};

	onMount(() => {
		calculateCorrectViewportHeight();
		setBaseFontSizeByScreenWidth();

		// We listen to the resize event
		window.addEventListener('resize', () => {
			calculateCorrectViewportHeight();
			setBaseFontSizeByScreenWidth();
		});

		pageOffset.subscribe((value) => {

			scroll({
				top: Math.max(0, (value - $toggleUniversalToPageScroll)),
				behavior: 'auto'	// instantly update the scroll position. Using 'smooth' should be used when jumping between anchors only!
			});

			if (value < $toggleUniversalToPageScroll) {
				universalScroll.set(true);
			} else {
				universalScroll.set(false);
			}
		});
	});
</script>

<style type="text/css">
	main {
		text-align: center;
		padding: 0;
		margin: 0 auto;
		min-height: 100vh;
		padding-top: 14vw;
	}

	@media (min-width: 640px) {
		main {
			padding-top: 8.5vw;
		}
	}

	h1 {
		color: var(--color-dove-grey);
		text-transform: uppercase;
		font-size: 4em;
		font-weight: 100;
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}

	.section-privacy {
		text-align: left;
		max-width: none;
	}

	@media (min-width: 640px) {
		.section-privacy {
			margin: 1.5rem auto 0;
			max-width: 400px;
			padding-bottom: 1.5rem;
		}
	}

	.imprint__list {
		list-style: none;
	}

	.imprint__list {
		padding-bottom: .75rem;
	}
</style>

<Link to="/">
	<TopNavigation route="impressum" animate={false}/>
</Link>
<main bind:this={_main}>
	<PureText>
		<section class="section-privacy">

			<h1 class="privacy__headline">Datenschutz­erklärung</h1>

			<p>Verantwortliche Stelle im Sinne der Datenschutzgesetze, insbesondere der EU-Datenschutz­grund­verord­nung
				(DSGVO), ist:
			</p>
			<ul class="imprint__list">
				<li><address class="address__company">Industrieverein Sachsen 1828 e.V.</address></li>
				<li><address class="address__name">Katrin Hoffmann</address></li>
				<li><address class="address__street">Zwickauer Straße 355a</address></li>
				<li><address class="address__zip imprint__list--one-line">09117 </address>
					<address class="address__city imprint__list--one-line"> Chemnitz</address></li>
				<li><address class="address__country">Deutschland</address></li>
			</ul>

			<h2 class="privacy__headline">Ihre Betroffenen­rechte</h2>
			<p>Unter den angegebenen Kontaktdaten unseres Datenschutzbeauftragten können Sie jederzeit folgende Rechte ausüben:</p>
			<ul class="privacy__list">
				<li class="privacy__list__item">Auskunft über Ihre bei uns gespeicherten Daten und deren Verarbeitung,</li>
				<li class="privacy__list__item">Berichtigung unrichtiger personenbezogener Daten,</li>
				<li class="privacy__list__item">Löschung Ihrer bei uns gespeicherten Daten,</li>
				<li class="privacy__list__item">Einschränkung der Datenverarbeitung, sofern wir Ihre Daten aufgrund gesetzlicher Pflichten noch nicht löschen
					dürfen,</li>
				<li class="privacy__list__item">Widerspruch gegen die Verarbeitung Ihrer Daten bei uns und</li>
				<li class="privacy__list__item">Datenübertragbarkeit, sofern Sie in die Datenverarbeitung eingewilligt haben oder einen Vertrag mit uns
					abgeschlossen haben.</li>
			</ul>
			<p>Sofern Sie uns eine Einwilligung erteilt haben, können Sie diese jederzeit mit Wirkung für die Zukunft widerrufen.</p>
			<p>Sie können sich jederzeit mit einer Beschwerde an die für Sie zuständige Aufsichtsbehörde wenden. Ihre zuständige
				Aufsichtsbehörde richtet sich nach dem Bundesland Ihres Wohnsitzes, Ihrer Arbeit oder der mutmaßlichen Verletzung.
				Eine Liste der Aufsichtsbehörden (für den nichtöffentlichen Bereich) mit Anschrift finden Sie unter:
				<a class="privacy__link" href="https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html">https://www.bfdi.bund.de/­DE/Infothek/­Anschriften_Links/­anschriften_links-node.html</a>
			</p>

			<h2 class="privacy__headline">Zwecke der Daten­verarbeitung durch die verantwortliche Stelle und Dritte</h2>
			<p>Wir verarbeiten Ihre personenbezogenen Daten nur zu den in dieser Datenschutzerklärung genannten Zwecken. Eine
				Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den genannten Zwecken findet nicht statt. Wir
				geben Ihre persönlichen Daten nur an Dritte weiter, wenn:
			</p>
			<ul class="privacy__list">
				<li class="privacy__list__item">Sie Ihre ausdrückliche Einwilligung dazu erteilt haben,</li>
				<li class="privacy__list__item">die Verarbeitung zur Abwicklung eines Vertrags mit Ihnen erforderlich ist,</li>
				<li class="privacy__list__item">die Verarbeitung zur Erfüllung einer rechtlichen Verpflichtung erforderlich ist,</li>
			</ul>
			<p>die Verarbeitung zur Wahrung berechtigter Interessen erforderlich ist und kein Grund zur Annahme besteht, dass Sie
				ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben.
			</p>

			<h2 class="privacy__headline">Löschung bzw. Sperrung der Daten</h2>
			<p>Wir halten uns an die Grundsätze der Datenvermeidung und Datensparsamkeit. Wir speichern Ihre personenbezogenen
				Daten daher nur so lange, wie dies zur Erreichung der hier genannten Zwecke erforderlich ist oder wie es die
				vom Gesetzgeber vorgesehenen vielfältigen Speicherfristen vorsehen. Nach Fortfall des jeweiligen Zweckes bzw.
				Ablauf dieser Fristen werden die entsprechenden Daten routinemäßig und entsprechend den gesetzlichen Vorschriften
				gesperrt oder gelöscht.
			</p>

			<h2 class="privacy__headline">SSL-Verschlüsselung</h2>
			<p>Um die Sicherheit Ihrer Daten bei der Übertragung zu schützen, verwenden wir dem aktuellen Stand der Technik
				entsprechende Verschlüsselungsverfahren (z. B. SSL) über HTTPS.
			</p>

			<h2 class="privacy__headline">Änderung unserer Datenschutz­bestimmungen</h2>
			<p>Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen
				Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen, z.B.
				bei der Einführung neuer Services. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
			</p>

			<h2 class="privacy__headline">Fragen an den Datenschutz­beauftragten</h2>
			<p>Wenn Sie Fragen zum Datenschutz haben, schreiben Sie uns bitte eine E-Mail: <a class="privacy__link" href="mailto:katrin.hoffmann@industrieverein.org">katrin.hoffmann@industrieverein.org</a>.
			</p>

			<p class="single-paragraph">Die Datenschutzerklärung wurde mit dem
				<a class="privacy__link" href="https://www.activemind.de/datenschutz/datenschutzhinweis-generator/">
					Datenschutzerklärungs-Generator der activeMind AG</a> erstellt.
			</p>

		</section>
	</PureText>
</main>
<BottomNavigation route="impressum" animate={false}/>
